import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Hook customizado para gerenciar operações relacionadas aos usuários
 * @returns {Object} Funções e estados para gerenciar usuários
 */
const useUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const usuarioAuthContext = useAuth();

  // Carregar todos os usuários
  const carregarUsuarios = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.get('/usuarios');
      if (response.data.success) {
        setUsuarios(response.data.data);
        return response.data.data;
      } else {
        console.error('Erro ao carregar usuários:', response.data.error);
        setErro(response.data.error || 'Erro ao carregar usuários');
        return [];
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setErro(error.message || 'Erro ao carregar usuários do servidor');
      return [];
    } finally {
      setCarregando(false);
    }
  };

  // Criar um novo usuário
  const criarUsuario = async (formData) => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.post('/usuarios', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        await carregarUsuarios();
        return { success: true, data: response.data.data };
      } else {
        setErro(response.data.error || 'Erro ao criar usuário');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao criar usuário. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Criar usuários em massa
  const criarUsuariosEmMassa = async (users) => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.post('/usuarios/bulk-create', { users });
      
      if (response.data.success) {
        await carregarUsuarios();
        return { 
          success: true, 
          created: response.data.created,
          total: response.data.total,
          errors: response.data.errors || []
        };
      } else {
        setErro(response.data.error || 'Erro ao criar usuários em massa');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao criar usuários em massa:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao criar usuários em massa. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Atualizar um usuário existente
  const atualizarUsuario = async (id, formData) => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.put(`/usuarios/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        await carregarUsuarios();
        return { success: true, data: response.data.data };
      } else {
        setErro(response.data.error || 'Erro ao atualizar usuário');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar usuário. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Excluir um usuário
  const excluirUsuario = async (id) => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.delete(`/usuarios/${id}`);
      
      if (response.data.success) {
        await carregarUsuarios();
        return { success: true };
      } else {
        setErro(response.data.error || 'Erro ao excluir usuário');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao excluir usuário. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Ativar/Desativar um usuário
  const toggleAtivoUsuario = async (id) => {
    try {
      setCarregando(true);
      setErro(null);
      
      // Primeiro, obtemos o usuário atual para verificar seu status
      const usuarioAtual = usuarios.find(user => user.id === id);
      if (!usuarioAtual) {
        throw new Error('Usuário não encontrado');
      }
      
      // Enviamos o novo status (oposto do atual)
      const response = await api.put(`/usuarios/${id}/status`, {
        ativo: !usuarioAtual.ativo
      });
      
      if (response.data.success) {
        await carregarUsuarios();
        return { success: true, ativo: !usuarioAtual.ativo };
      } else {
        setErro(response.data.error || `Erro ao ${usuarioAtual.ativo ? 'desativar' : 'ativar'} usuário`);
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao atualizar status do usuário:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar status do usuário. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Atualizar apenas o guichê de um usuário
  const atualizarGuicheUsuario = async (id, guiche) => {
    try {
      setCarregando(true);
      setErro(null);
      
      // Criar objeto de dados em vez de FormData
      const data = {
        guiche: guiche === '' ? null : guiche
      };
      
      const response = await api.put(`/usuarios/${id}/guiche`, data);
      
      if (response.data.success) {
        await carregarUsuarios();
        return { success: true, data: response.data.data };
      } else {
        setErro(response.data.error || 'Erro ao atualizar guichê');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao atualizar guichê:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar guichê. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Atualizar serviços atendidos de um usuário
  const atualizarServicosUsuario = async (id, servicosAtendidos) => {
    try {
      setCarregando(true);
      setErro(null);
      
      // Criar objeto de dados em vez de FormData
      const data = {
        servicos_atendidos: servicosAtendidos
      };
      
      const response = await api.put(`/usuarios/${id}/servicos`, data);
      
      if (response.data.success) {
        try {
          // Buscar dados atualizados do usuário - usando o endpoint correto
          const usuarioResponse = await api.get(`/usuarios/${id}`);
          
          // Se o usuário logado é o mesmo que está sendo editado
          // Atualizar os dados no contexto através da AuthContext
          if (usuarioResponse.data.success) {
            if (id === usuarioAuthContext.user?.id) {
              usuarioAuthContext.updateUser({
                servicos_atendidos: usuarioResponse.data.data.servicos_atendidos
              });
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados atualizados do usuário:', error);
          // Se falhar ao buscar dados atualizados, usar os dados da resposta original
          if (id === usuarioAuthContext.user?.id) {
            usuarioAuthContext.updateUser({
              servicos_atendidos: servicosAtendidos
            });
          }
        }
        
        await carregarUsuarios();
        return { success: true, data: response.data.data };
      } else {
        setErro(response.data.error || 'Erro ao atualizar serviços');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erro ao atualizar serviços:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar serviços. Por favor, tente novamente.';
      setErro(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCarregando(false);
    }
  };

  // Carregar usuários ao montar o componente
  useEffect(() => {
    carregarUsuarios();
  }, []);

  return {
    usuarios,
    carregando,
    erro,
    carregarUsuarios,
    criarUsuario,
    atualizarUsuario,
    excluirUsuario,
    atualizarGuicheUsuario,
    atualizarServicosUsuario,
    toggleAtivoUsuario,
    criarUsuariosEmMassa
  };
};

export default useUsuarios; 